/**
 * Geometry layer contracts.
 *
 * The timeline evaluator is kernel-agnostic: it hands a feature plus its
 * resolved upstream results to a GeomKernel and stores whatever comes back.
 * Step 4 provides the built-in JS kernel (sketch tessellation + extrude);
 * the OpenCascade.js WASM kernel slots in behind the same interface for
 * STEP-grade booleans, fillets, and shells — running in a Web Worker.
 */

import type { FeatureId, FeatureNode, Vec2 } from '../timeline/types';

/** Tessellated render mesh — transferable to workers and the GPU as-is. */
export interface MeshData {
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly indices: Uint32Array;
  /** Polyline segments for crisp CAD edge rendering (pairs of xyz points). */
  readonly edgePositions: Float32Array;
  /**
   * TRUE topological vertices (B-rep kernel only). When present, snapping
   * treats these as the ONLY 'endpoint' candidates — polyline joints along
   * discretized arcs are curve samples, not corners.
   */
  readonly vertexPositions?: Float32Array;
  /** B-rep edge index per edgePositions SEGMENT (edge picking; B-rep only). */
  readonly edgeGroups?: Uint32Array;
  /** Curve midpoint per B-rep edge — a pick's regeneration-stable identity. */
  readonly edgeMidpoints?: Float32Array;
  /** Per-face kind: 0 other, 1 planar, 2 cylindrical (B-rep only). */
  readonly faceKinds?: Uint8Array;
  /** Per-face [10]: centroid(3), normal/axis-dir(3), axis point(3), radius. */
  readonly faceData?: Float32Array;
  /** B-rep face index per display triangle — face picking. */
  readonly triFaceIndex?: Uint32Array;
}

/**
 * A feature's evaluated output. `kind` is open-ended:
 *  - 'body'    solid/surface body with a render mesh (and, later, a B-rep handle)
 *  - 'sketch'  resolved profile regions ready for extrude/revolve
 *  - 'empty'   features that only modify upstream bodies in place
 */
export type GeometryResult =
  | {
      readonly kind: 'body';
      readonly mesh: MeshData;
      /**
       * Upstream body-features this result replaced (boolean ops absorb
       * their targets). Consumed results stay cached but are not displayed.
       */
      readonly consumed?: readonly FeatureId[];
      /**
       * Stable-named planar faces (extrudes: 'face:top'/'face:bottom' caps).
       * Downstream sketches attach here and ride through regeneration.
       */
      readonly capPlanes?: { readonly top: Float32Array; readonly bottom: Float32Array };
      /** Registry key of the exact B-rep shape (present on the OCCT kernel). */
      readonly brepId?: string;
      /**
       * Part features only: each inserted component's resolved material (from
       * its SOURCE project) + mesh volume, in insertion order. Lets
       * assemblies render/weigh components without owning their timelines.
       */
      readonly componentMaterials?: readonly {
        material?: string;
        color?: string;
        volume: number;
      }[];
    }
  | {
      readonly kind: 'sketch';
      readonly profiles: readonly ProfileRegion[];
      /** Open (unclosed) chains — sweep paths, construction curves. Sketch UV. */
      readonly paths: readonly (readonly Vec2[])[];
      /** Resolved plane-to-world matrix (face-attached planes resolve here). */
      readonly planeWorld: Float32Array;
      /** Raw entities — the B-rep kernel rebuilds TRUE curves from these. */
      readonly entities: readonly import('../timeline/types').SketchEntity[];
    }
  | { readonly kind: 'empty' };

/** A closed planar region extracted from sketch entities, in sketch-plane UV. */
export interface ProfileRegion {
  /** Outer loop as a polygon (tessellated arcs/circles), CCW. */
  readonly outer: readonly (readonly [number, number])[];
  /** Holes, CW. */
  readonly holes: readonly (readonly (readonly [number, number])[])[];
  /** Plane-to-world transform, column-major 4x4. */
  readonly planeToWorld: Float32Array;
}

export interface EvalContext {
  /** Resolved outputs of the feature's dependencies, keyed by feature id. */
  readonly inputs: ReadonlyMap<FeatureId, GeometryResult>;
  /**
   * All bodies visible at this point in the timeline (earlier body results
   * not yet consumed by a boolean). Implicit targets for join/cut/intersect.
   */
  readonly worldBodies: ReadonlyMap<FeatureId, { mesh: MeshData; brepId?: string }>;
}

export interface TopologySummary {
  solids: number;
  shells: number;
  faces: Record<string, number>;
  edges: Record<string, number>;
  wires: number;
  vertices: number;
}

export interface WallThicknessResult {
  min: number;
  max: number;
  mean: number;
  samples: number;
  thinnest: { point: [number, number, number]; thickness: number }[];
}

export interface MeasureResult {
  face?: {
    kind: string; area: number; centroid: [number, number, number];
    normal?: [number, number, number]; radius?: number; diameter?: number;
    axis?: [number, number, number]; axialLength?: number; distance: number;
  };
  edge?: {
    kind: string; length: number; radius?: number; diameter?: number;
    endpoints: [[number, number, number], [number, number, number]]; distance: number;
  };
}

export interface GeomKernel {
  execute(feature: FeatureNode, ctx: EvalContext): Promise<GeometryResult>;
  /** Optional: drop kernel-side state for features no longer in the doc. */
  prune?(liveIds: readonly string[]): void;
  /** Optional: export exact B-rep solids as STEP text. */
  exportStep?(brepIds: readonly string[], names?: readonly string[]): Promise<string>;
  /** Optional (B-rep only): exact volume/area/center of mass of a body. */
  massProps?(
    brepId: string,
  ): Promise<{ volume: number; area: number; centerOfMass: [number, number, number] }>;
  /** Optional (B-rep only): exact overlap volume between two bodies. */
  interference?(brepIdA: string, brepIdB: string): Promise<number>;
  /** Optional (B-rep only): topology census (solids/faces/edges by kind). */
  topologySummary?(brepId: string): Promise<TopologySummary>;
  /** Optional (B-rep only): sampled wall-thickness distribution. */
  wallThickness?(brepId: string, samples?: number): Promise<WallThicknessResult>;
  /** Optional (B-rep only): plane∩body cut segments in 2D plane coords. */
  crossSection?(brepId: string, axis: 'X' | 'Y' | 'Z', offset: number): Promise<Float32Array>;
  /** Optional (B-rep only): HLR projection → visible/hidden/circle geometry. */
  projectView?(
    brepIds: readonly string[],
    dir: [number, number, number],
    xdir: [number, number, number],
    section?: { axis: 'X' | 'Y' | 'Z'; offset: number; flip: boolean },
  ): Promise<{
    visible: Float32Array;
    hidden: Float32Array;
    circles: Float32Array;
    sectionSegs: Float32Array;
  }>;
  /** Optional (B-rep only): measure the face/edge nearest a point. */
  measureNear?(
    brepId: string,
    point: [number, number, number],
    want: 'auto' | 'face' | 'edge',
  ): Promise<MeasureResult>;
  /** Optional (B-rep only): exact min distance + witness points. */
  distance?(
    brepIdA: string,
    brepIdB: string,
  ): Promise<{ distance: number; pointA: [number, number, number]; pointB: [number, number, number] }>;
}

/** Thrown by kernels for user-fixable failures (bad profile, zero distance…). */
export class FeatureError extends Error {
  constructor(
    public readonly featureId: FeatureId,
    message: string,
  ) {
    super(message);
    this.name = 'FeatureError';
  }
}
